import PageHeader from "@/components/site/PageHeader";
import { TEAM } from "@/lib/content";
import { motion } from "framer-motion";
import { useSEO } from "@/lib/seo";
import { Phone, Mail, Stamp } from "lucide-react";

const TEAM_IMG =
    "https://images.unsplash.com/photo-1758520145147-c30bc656f314";

export default function Medarbetare() {
    useSEO({
        title: "Medarbetare",
        description:
            "Möt BM Juridiks jurister — ett erfaret team med bred branschkunskap och skarp juridisk analysförmåga.",
        image: TEAM_IMG,
    });
    return (
        <div data-testid="page-medarbetare">
            <PageHeader
                kicker="Medarbetare"
                title="Jurister som"
                accent="ser helheten."
                description="Ett erfaret team med bred branschkunskap och skarp juridisk analysförmåga. Tillsammans bildar vi den rådgivare våra klienter väljer — om och om igen."
                image={TEAM_IMG}
            />

            <section className="py-20 bg-[#0A0A0B]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {TEAM.map((m, i) => (
                            <motion.div
                                key={m.name}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: i * 0.05 }}
                                data-testid={`team-card-${i}`}
                                className="group bg-[#111113] border border-white/5 hover:border-[#C4A977]/40 rounded-sm overflow-hidden transition-colors duration-500"
                            >
                                <div className="aspect-[4/5] relative overflow-hidden bg-[#18181B]">
                                    <img
                                        src={m.photo || TEAM_IMG}
                                        alt={m.name}
                                        className={`w-full h-full object-cover ${m.photo ? "" : "opacity-80 grayscale group-hover:grayscale-0 group-hover:opacity-100"} transition-all duration-700`}
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-[#111113] via-[#111113]/40 to-transparent" />
                                    <div className="absolute bottom-4 left-4 right-4">
                                        <p className="overline text-[#C4A977]">
                                            {m.role}
                                        </p>
                                    </div>
                                </div>
                                <div className="p-8">
                                    <h3 className="font-serif text-2xl text-[#F4F4F5]">
                                        {m.name}
                                    </h3>
                                    {m.notarius && (
                                        <span
                                            data-testid={`team-notarius-${i}`}
                                            className="mt-3 inline-flex items-center gap-2 px-3 py-1 text-[11px] uppercase tracking-widest bg-[#C4A977]/10 border border-[#C4A977]/40 text-[#C4A977]"
                                        >
                                            <Stamp size={11} /> Notarius Publicus
                                        </span>
                                    )}
                                    <p className="mt-3 text-sm text-[#A1A1AA] leading-relaxed">
                                        {m.bio}
                                    </p>
                                    <div className="mt-6 flex flex-wrap gap-2">
                                        {m.focus.map((f) => (
                                            <span
                                                key={f}
                                                className="px-3 py-1 text-xs border border-white/10 text-[#A1A1AA] tracking-wide"
                                            >
                                                {f}
                                            </span>
                                        ))}
                                    </div>
                                    {m.phone && (
                                        <a
                                            href={`tel:${m.phoneTel}`}
                                            data-testid={`team-phone-${i}`}
                                            className="mt-6 inline-flex items-center gap-2 text-sm text-[#C4A977] hover:text-[#DBC08F] transition-colors"
                                        >
                                            <Phone size={14} /> {m.phone}
                                        </a>
                                    )}
                                    {m.email && (
                                        <a
                                            href={`mailto:${m.email}`}
                                            data-testid={`team-email-${i}`}
                                            className={`${m.phone ? "mt-2" : "mt-6"} flex items-center gap-2 text-sm text-[#C4A977] hover:text-[#DBC08F] transition-colors`}
                                        >
                                            <Mail size={14} /> {m.email}
                                        </a>
                                    )}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
